import { useState, useRef, Component, ErrorInfo } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Settings, UtensilsCrossed,
  List, Layers, Plus, Trash2, Pencil, RefreshCw, Copy,
  ImageIcon, ChevronDown, ChevronUp, AlertTriangle, Loader2,
  ToggleLeft, ToggleRight, Link2, Unlink2, Save, ShieldAlert, FolderOpen,
  LayoutPanelLeft, Info,
} from "lucide-react";
import MenuEditorTab from "./MenuEditorTab";
import AdminLayout from "./AdminLayout";
import { useLAMToken } from "@/hooks/useLAMToken";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

class MenuEditorErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[MenuEditorTab] Render error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <AlertTriangle className="w-8 h-8 text-destructive" />
          <p className="text-sm font-medium">Something went wrong in the menu editor.</p>
          <p className="text-xs text-muted-foreground">Reload the page to try again.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function lamHeaders(token: string): Record<string, string> {
  const jwt = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return {
    "Content-Type": "application/json",
    "x-eatzy-token": token,
    ...(jwt ? { "Authorization": `Bearer ${jwt}` } : {}),
  };
}

async function lamFetch(url: string, token: string, opts: RequestInit = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { ...lamHeaders(token), ...(opts.headers as Record<string, string> || {}) },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

function TabError({ error, label }: { error: unknown; label?: string }) {
  const msg = error instanceof Error ? error.message : String(error);
  const is401or403 = msg.startsWith("401") || msg.startsWith("403");
  return (
    <div className="py-8 flex flex-col items-center gap-3 text-center">
      <AlertTriangle className="w-8 h-8 text-destructive" />
      <p className="text-sm font-medium text-destructive">
        {is401or403
          ? "Access denied — your session token does not have permission for this action."
          : `Failed to load ${label ?? "data"}.`}
      </p>
      <p className="text-xs text-muted-foreground font-mono max-w-md break-all">{msg}</p>
      {is401or403 && (
        <p className="text-xs text-muted-foreground">
          Sign out and sign back in to refresh your Eatzy credentials, then try again.
        </p>
      )}
    </div>
  );
}

// ─── CONFIG TAB ───────────────────────────────────────────────────────────────

function ConfigTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, any> | null>(null);

  const { data: config, isLoading, isError, error } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "config"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/config`, token),
    enabled: !!token,
  });

  const { data: taxProfilesData } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "taxprofiles"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/taxprofiles`, token),
    enabled: !!token,
  });

  const taxProfiles: any[] = taxProfilesData?.tax_profiles ?? [];

  if (config && !form) {
    setForm({
      zone_id: config.zone_id ?? "",
      tax_profile_id: config.tax_profile_id ?? "",
      tax_exempt: !!config.tax_exempt,
      place_time: config.place_time ?? "",
      prep_time: config.prep_time ?? "",
      fax_time: config.fax_time ?? "",
      check_time: config.check_time ?? "",
      description: config.description ?? "",
      address: config.address ?? "",
      city: config.city ?? "",
      state: config.state ?? "",
      zip: config.zip ?? "",
      phone: config.phone ?? "",
      primary_phone: config.primary_phone ?? "",
      is_csr_only: !!config.is_csr_only,
      paid_by: config.paid_by ?? "",
      pmnt_periodicity: config.pmnt_periodicity ?? "",
      abbrev_name: config.abbrev_name ?? "",
      admin_note: config.admin_note ?? "",
    });
  }

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any>) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/config`, token, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      toast({ title: "Config saved" });
      qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "config"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const payload = {
      ...form,
      tax_exempt: form.tax_exempt ? 1 : 0,
      is_csr_only: form.is_csr_only ? 1 : 0,
      zone_id: form.zone_id ? Number(form.zone_id) : undefined,
      tax_profile_id: form.tax_profile_id ? Number(form.tax_profile_id) : undefined,
      place_time: form.place_time ? Number(form.place_time) : undefined,
      prep_time: form.prep_time ? Number(form.prep_time) : undefined,
      fax_time: form.fax_time ? Number(form.fax_time) : undefined,
      check_time: form.check_time ? Number(form.check_time) : undefined,
    };
    saveMutation.mutate(payload);
  }

  if (isError) return <TabError error={error} label="config" />;
  if (isLoading || !form) return <div className="py-8 text-center text-muted-foreground">Loading config…</div>;

  function field(key: string) {
    return {
      value: form![key] ?? "",
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f: any) => ({ ...f, [key]: e.target.value })),
    };
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Abbreviated Name</Label>
          <Input {...field("abbrev_name")} placeholder="e.g. MR" />
        </div>
        <div className="space-y-2">
          <Label>Zone ID</Label>
          <Input type="number" {...field("zone_id")} placeholder="Zone ID" />
        </div>
        <div className="space-y-2">
          <Label>Tax Profile</Label>
          <Select
            value={String(form.tax_profile_id ?? "")}
            onValueChange={(v) => setForm((f: any) => ({ ...f, tax_profile_id: v ? Number(v) : "" }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select tax profile" />
            </SelectTrigger>
            <SelectContent>
              {taxProfiles.map((tp: any) => (
                <SelectItem key={tp.id} value={String(tp.id)}>
                  {tp.name} {tp.default ? "(default)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Payment Periodicity</Label>
          <Select
            value={form.pmnt_periodicity}
            onValueChange={(v) => setForm((f: any) => ({ ...f, pmnt_periodicity: v }))}
          >
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Bi-Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Paid By</Label>
          <Input {...field("paid_by")} placeholder="e.g. restaurant" />
        </div>
        <div className="space-y-2">
          <Label>Place Time (min)</Label>
          <Input type="number" {...field("place_time")} />
        </div>
        <div className="space-y-2">
          <Label>Prep Time (min)</Label>
          <Input type="number" {...field("prep_time")} />
        </div>
        <div className="space-y-2">
          <Label>Check Time (min)</Label>
          <Input type="number" {...field("check_time")} />
        </div>
        <div className="space-y-2">
          <Label>Fax Time (min)</Label>
          <Input type="number" {...field("fax_time")} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Street Address</Label>
          <Input {...field("address")} />
        </div>
        <div className="space-y-2">
          <Label>City</Label>
          <Input {...field("city")} />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Input {...field("state")} />
        </div>
        <div className="space-y-2">
          <Label>Zip</Label>
          <Input {...field("zip")} />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input {...field("phone")} />
        </div>
        <div className="space-y-2">
          <Label>Primary Phone</Label>
          <Input {...field("primary_phone")} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea {...field("description")} rows={3} />
      </div>
      <div className="space-y-2">
        <Label>Admin Note</Label>
        <Textarea {...field("admin_note")} rows={2} />
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <Switch
            checked={form.tax_exempt}
            onCheckedChange={(v) => setForm((f: any) => ({ ...f, tax_exempt: v }))}
          />
          <Label>Tax Exempt</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_csr_only}
            onCheckedChange={(v) => setForm((f: any) => ({ ...f, is_csr_only: v }))}
          />
          <Label>CSR Only</Label>
        </div>
      </div>

      <Button type="submit" disabled={saveMutation.isPending}>
        <Save className="w-4 h-4 mr-2" />
        {saveMutation.isPending ? "Saving…" : "Save Config"}
      </Button>
    </form>
  );
}

// ─── PRICE LEVEL TAB ─────────────────────────────────────────────────────────

const SERVICE_TYPES = [
  { key: "delivery", label: "Delivery" },
  { key: "takeout", label: "Takeout" },
  { key: "catering", label: "Catering" },
];

const PRICE_TIERS = [
  { key: "wholesale", label: "Wholesale", section: "contract" },
  { key: "standard", label: "Standard", section: "customer" },
  { key: "hotel", label: "Hotel", section: "customer" },
  { key: "vip", label: "Vip", section: "customer" },
  { key: "alt1", label: "Alt1", section: "customer" },
  { key: "alt2", label: "Alt2", section: "customer" },
];

function PriceLevelTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, any> | null>(null);
  const [showUpdateWarning, setShowUpdateWarning] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "pricelevel"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/pricelevel`, token),
    enabled: !!token,
  });

  if (data && !form) setForm({ ...(data.margins ?? data) });

  const saveMutation = useMutation({
    mutationFn: async ({ updateItems }: { updateItems: boolean }) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/pricelevel`, token, {
        method: "POST",
        body: JSON.stringify({ margins: form, update_items: updateItems }),
      }),
    onSuccess: (_, { updateItems }) => {
      toast({ title: updateItems ? "Prices updated and items recalculated" : "Price level saved" });
      qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "pricelevel"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  if (isError) return <TabError error={error} label="price level" />;
  if (isLoading || !form) return <div className="py-8 text-center text-muted-foreground">Loading price level…</div>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Set the percentage for each service type and price tier. Values above 100 represent a markup (e.g. 110 = 10% markup); values below 100 represent a discount (e.g. 80 = 20% discount).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SERVICE_TYPES.map(({ key: svc, label: svcLabel }) => {
          const contractTiers = PRICE_TIERS.filter(t => t.section === "contract");
          const customerTiers = PRICE_TIERS.filter(t => t.section === "customer");
          return (
            <div key={svc} className="space-y-3">
              <h3 className="font-semibold text-sm border-b pb-2">{svcLabel}</h3>

              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Contract rate: amount paid to restaurant</p>
              {contractTiers.map(({ key: tier, label }) => (
                <div key={tier} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={form[`${svc}_${tier}`] ?? ""}
                    onChange={(e) => setForm((f: any) => ({ ...f, [`${svc}_${tier}`]: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
              ))}

              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide pt-1">Prices charged to customers</p>
              {customerTiers.map(({ key: tier, label }) => (
                <div key={tier} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={form[`${svc}_${tier}`] ?? ""}
                    onChange={(e) => setForm((f: any) => ({ ...f, [`${svc}_${tier}`]: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate({ updateItems: false })}
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? "Saving…" : "Save and Continue"}
        </Button>
        <Button
          variant="default"
          disabled={saveMutation.isPending}
          onClick={() => setShowUpdateWarning(true)}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Save and Update All Prices
        </Button>
      </div>

      <AlertDialog open={showUpdateWarning} onOpenChange={setShowUpdateWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate all item prices?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save the new margins and recalculate the full 21-price matrix for every menu item
              at this restaurant. This may take a moment and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowUpdateWarning(false); saveMutation.mutate({ updateItems: true }); }}>
              Update All Prices
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── ROUNDING TAB ─────────────────────────────────────────────────────────────

function RoundingTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, any> | null>(null);
  const [showUpdateWarning, setShowUpdateWarning] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "rounding"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/rounding`, token),
    enabled: !!token,
  });

  if (data && !form) {
    setForm({
      ...data,
      round_quarter: Number(data.round_quarter) || 0,
      last_digit: data.last_digit != null ? Number(data.last_digit) : null,
      use_rounding: !!data.use_rounding && data.use_rounding !== "No",
    });
  }

  const saveMutation = useMutation({
    mutationFn: async ({ updateItems }: { updateItems: boolean }) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/rounding`, token, {
        method: "POST",
        body: JSON.stringify({ ...form, update_items: updateItems }),
      }),
    onSuccess: (_, { updateItems }) => {
      toast({ title: updateItems ? "Rounding saved and item prices recalculated" : "Rounding rules saved" });
      qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "rounding"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  if (isError) return <TabError error={error} label="rounding rules" />;
  if (isLoading || !form) return <div className="py-8 text-center text-muted-foreground">Loading rounding rules…</div>;

  const roundMode: "last_digit" | "quarter" = form.round_quarter ? "quarter" : "last_digit";

  function setRoundMode(mode: "last_digit" | "quarter") {
    if (mode === "quarter") {
      setForm((f: any) => ({ ...f, round_quarter: 1, last_digit: null }));
    } else {
      setForm((f: any) => ({ ...f, round_quarter: 0, last_digit: f.last_digit ?? 9 }));
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Use rounding rules</Label>
          <Select
            value={form.use_rounding ? "Yes" : "No"}
            onValueChange={(v) => setForm((f: any) => ({ ...f, use_rounding: v === "Yes" }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Round to .99 when above</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.round_above ?? ""}
              onChange={(e) => setForm((f: any) => ({ ...f, round_above: e.target.value }))}
            />
            <span className="text-xs text-muted-foreground whitespace-nowrap">If between .01 and .09 will be rounded to $K.99</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Label>Rounding mode</Label>
        <RadioGroup
          value={roundMode}
          onValueChange={(v) => setRoundMode(v as "last_digit" | "quarter")}
          className="space-y-3"
        >
          <div className="flex items-center gap-3">
            <RadioGroupItem value="last_digit" id="mode-last-digit" />
            <label htmlFor="mode-last-digit" className="text-sm cursor-pointer">Last digit:</label>
            <Input
              type="number"
              min={0}
              max={9}
              disabled={roundMode !== "last_digit"}
              value={roundMode === "last_digit" ? (form.last_digit ?? "") : ""}
              onChange={(e) => setForm((f: any) => ({ ...f, last_digit: Number(e.target.value) }))}
              className="h-8 w-20 text-sm"
            />
            <span className="text-xs text-muted-foreground">Last digit of all prices will be changed to this number</span>
          </div>
          <div className="flex items-center gap-3">
            <RadioGroupItem value="quarter" id="mode-quarter" />
            <label htmlFor="mode-quarter" className="text-sm cursor-pointer">Round to .25, .50, .75, .95</label>
          </div>
        </RadioGroup>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate({ updateItems: false })}
        >
          <Save className="w-4 h-4 mr-2" />
          Save and Continue
        </Button>
        <Button
          disabled={saveMutation.isPending}
          onClick={() => setShowUpdateWarning(true)}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Save and Update All Prices
        </Button>
      </div>

      <AlertDialog open={showUpdateWarning} onOpenChange={setShowUpdateWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate all item prices?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save the rounding rules and recalculate the price of every menu item.
              This may take a moment and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowUpdateWarning(false); saveMutation.mutate({ updateItems: true }); }}>
              Update All Prices
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── MENUS TAB ────────────────────────────────────────────────────────────────

function formatSecsToHHMM(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

function formatMenuField(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === "string") return item;
      if (
        typeof item === "object" &&
        item !== null &&
        "start" in item &&
        "end" in item &&
        typeof (item as Record<string, unknown>).start === "number" &&
        typeof (item as Record<string, unknown>).end === "number"
      ) {
        const slot = item as Record<string, number>;
        return `${formatSecsToHHMM(slot.start)}–${formatSecsToHHMM(slot.end)}`;
      }
      return JSON.stringify(item);
    }).join(", ");
  }
  return JSON.stringify(value);
}

function MenusTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [availabilityDialog, setAvailabilityDialog] = useState<any>(null);
  const [deleteMenuConfirm, setDeleteMenuConfirm] = useState<any>(null);
  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set());
  const [newMenuForm, setNewMenuForm] = useState({ name: "", type: "", availability_type: "", available_days: "", available_times: "", available_timezone: "" });
  const [availForm, setAvailForm] = useState({ availability_type: "", available_days: "", available_times: "", available_timezone: "" });

  const { data: menus = [], isLoading, isError, error } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus`, token),
    enabled: !!token,
  });

  const invalidateMenus = () => qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"] });

  const addMenuMutation = useMutation({
    mutationFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus`, token, { method: "PUT", body: JSON.stringify(newMenuForm) }),
    onSuccess: () => { toast({ title: "Menu added" }); setAddMenuOpen(false); setNewMenuForm({ name: "", type: "", availability_type: "", available_days: "", available_times: "", available_timezone: "" }); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteMenuMutation = useMutation({
    mutationFn: (menuId: number) => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus/${menuId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Menu deleted" }); setDeleteMenuConfirm(null); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const availMutation = useMutation({
    mutationFn: ({ menuId }: { menuId: number }) => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus/${menuId}/availability`, token, { method: "POST", body: JSON.stringify(availForm) }),
    onSuccess: () => { toast({ title: "Availability updated" }); setAvailabilityDialog(null); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  if (isError) return <TabError error={error} label="menus" />;
  if (isLoading) return <div className="py-8 text-center text-muted-foreground">Loading menus…</div>;

  const menuList = Array.isArray(menus) ? menus : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{menuList.length} menu(s) — Menus control when items are available. Manage item categories in the Categories tab.</span>
        <Button size="sm" onClick={() => setAddMenuOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add Menu
        </Button>
      </div>

      {menuList.map((menu: any) => {
        const menuId = menu.info?.id ?? menu.id;
        const menuName = menu.info?.name ?? menu.name;
        const isExpanded = expandedMenus.has(menuId);
        return (
          <Card key={menuId}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{menuName}</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    {menu.info?.type && <span className="mr-2">Type: {menu.info.type}</span>}
                    {menu.info?.available_days && <span>Days: {formatMenuField(menu.info.available_days)}</span>}
                    {menu.info?.available_times && <span className="ml-2">Times: {formatMenuField(menu.info.available_times)}</span>}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => {
                    setAvailForm({ availability_type: menu.info?.availability_type ?? "", available_days: formatMenuField(menu.info?.available_days), available_times: formatMenuField(menu.info?.available_times), available_timezone: menu.info?.available_timezone ?? "" });
                    setAvailabilityDialog(menu);
                  }}>
                    <Settings className="w-3 h-3 mr-1" /> Availability
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setExpandedMenus(s => { const n = new Set(s); n.has(menuId) ? n.delete(menuId) : n.add(menuId); return n; })}>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteMenuConfirm(menu)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {isExpanded && (
              <CardContent>
                <div className="space-y-1 text-xs text-muted-foreground">
                  {menu.info?.availability_type && <div><span className="font-medium">Availability type:</span> {menu.info.availability_type}</div>}
                  {menu.info?.available_timezone && <div><span className="font-medium">Timezone:</span> {menu.info.available_timezone}</div>}
                  {menu.info?.available_days && <div><span className="font-medium">Days:</span> {formatMenuField(menu.info.available_days)}</div>}
                  {menu.info?.available_times && <div><span className="font-medium">Times:</span> {formatMenuField(menu.info.available_times)}</div>}
                  {!menu.info?.availability_type && !menu.info?.available_days && !menu.info?.available_times && (
                    <p>No availability details — click Availability to configure.</p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Add Menu Dialog */}
      <Dialog open={addMenuOpen} onOpenChange={setAddMenuOpen}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0"><DialogTitle>Add Menu</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={newMenuForm.name} onChange={e => setNewMenuForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Type</Label><Input value={newMenuForm.type} onChange={e => setNewMenuForm(f => ({ ...f, type: e.target.value }))} placeholder="e.g. regular" /></div>
            <div className="space-y-1"><Label>Availability Type</Label><Input value={newMenuForm.availability_type} onChange={e => setNewMenuForm(f => ({ ...f, availability_type: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Available Days</Label><Input value={newMenuForm.available_days} onChange={e => setNewMenuForm(f => ({ ...f, available_days: e.target.value }))} placeholder="Mon,Tue,Wed" /></div>
            <div className="space-y-1"><Label>Available Times</Label><Input value={newMenuForm.available_times} onChange={e => setNewMenuForm(f => ({ ...f, available_times: e.target.value }))} placeholder="11:00-15:00" /></div>
            <div className="space-y-1"><Label>Timezone</Label><Input value={newMenuForm.available_timezone} onChange={e => setNewMenuForm(f => ({ ...f, available_timezone: e.target.value }))} placeholder="America/New_York" /></div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
            <Button variant="outline" onClick={() => setAddMenuOpen(false)}>Cancel</Button>
            <Button onClick={() => addMenuMutation.mutate()} disabled={!newMenuForm.name || addMenuMutation.isPending}>
              {addMenuMutation.isPending ? "Adding…" : "Add Menu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={!!availabilityDialog} onOpenChange={v => !v && setAvailabilityDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Menu Availability</DialogTitle><DialogDescription>{availabilityDialog?.info?.name ?? availabilityDialog?.name}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Availability Type</Label><Input value={availForm.availability_type} onChange={e => setAvailForm(f => ({ ...f, availability_type: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Available Days</Label><Input value={availForm.available_days} onChange={e => setAvailForm(f => ({ ...f, available_days: e.target.value }))} placeholder="Mon,Tue,Wed" /></div>
            <div className="space-y-1"><Label>Available Times</Label><Input value={availForm.available_times} onChange={e => setAvailForm(f => ({ ...f, available_times: e.target.value }))} placeholder="11:00-15:00" /></div>
            <div className="space-y-1"><Label>Timezone</Label><Input value={availForm.available_timezone} onChange={e => setAvailForm(f => ({ ...f, available_timezone: e.target.value }))} placeholder="America/New_York" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityDialog(null)}>Cancel</Button>
            <Button onClick={() => availMutation.mutate({ menuId: availabilityDialog?.info?.id ?? availabilityDialog?.id })} disabled={availMutation.isPending}>
              {availMutation.isPending ? "Saving…" : "Save Availability"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Menu Confirm */}
      <AlertDialog open={!!deleteMenuConfirm} onOpenChange={v => !v && setDeleteMenuConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete menu?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteMenuConfirm?.info?.name ?? deleteMenuConfirm?.name}" will be removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMenuMutation.mutate(deleteMenuConfirm?.info?.id ?? deleteMenuConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── EXPANDED ITEM DETAIL (presentational — receives pre-loaded data as props) ─

function ExpandedItemDetail({
  itemId, itemName, linkedQuestionIds, allQuestions, allAnswers, onLinkRequest, onUnlink, unlinkPending,
}: {
  itemId: number;
  itemName?: string;
  linkedQuestionIds: number[];
  allQuestions: any[];
  allAnswers: any[];
  onLinkRequest: (fullItem: any) => void;
  onUnlink: (questionId: number) => void;
  unlinkPending: boolean;
}) {
  const linkedQuestions = allQuestions.filter((q: any) => linkedQuestionIds.includes(q.id));

  function answersFor(q: any): any[] {
    if (Array.isArray(q.answers)) return q.answers;
    if (Array.isArray(q.answer)) return q.answer;
    return allAnswers.filter((a: any) => a.parent === q.id || a.question_id === q.id || a.question === q.id);
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs font-medium text-muted-foreground">
          Linked Modifiers ({linkedQuestions.length})
        </span>
        <Button size="sm" variant="outline" onClick={() => onLinkRequest({ id: itemId, name: itemName, questions: linkedQuestionIds })}>
          <Plus className="w-3 h-3 mr-1" /> Add Modifier
        </Button>
      </div>
      {linkedQuestions.length === 0 ? (
        <p className="text-xs text-muted-foreground">No modifier groups linked.</p>
      ) : (
        <div className="space-y-1">
          {linkedQuestions.map((q: any) => (
            <div key={q.id} className="flex items-center justify-between gap-2 py-1 border-b last:border-0">
              <div>
                <span className="text-xs font-medium">{q.name}</span>
                {q.text && <span className="text-xs text-muted-foreground ml-1">· {q.text}</span>}
                {answersFor(q).length > 0 && (
                  <span className="text-xs text-muted-foreground ml-1">· {answersFor(q).length} answer{answersFor(q).length !== 1 ? "s" : ""}</span>
                )}
              </div>
              <Button size="icon" variant="ghost" onClick={() => onUnlink(q.id)} disabled={unlinkPending}>
                <Unlink2 className="w-3 h-3 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ITEMS TAB ────────────────────────────────────────────────────────────────

function ItemsTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [selectedMenuId, setSelectedMenuId] = useState<string>("");
  const [editItem, setEditItem] = useState<any>(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState<any>(null);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [itemForm, setItemForm] = useState({ name: "", description: "", price: "", category: "", service: ["takeout", "delivery"] as string[], sort: "", type: "", meta_category: "", feeds_from: "", feeds_to: "", prep_time: "", csr_note: "" });
  const [linkModifierItem, setLinkModifierItem] = useState<any>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);

  const { data: menus = [] } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus`, token),
    enabled: !!token,
  });
  const menuList = Array.isArray(menus) ? menus : [];

  // Dedicated categories query (restaurant-level, independent of menus)
  const { data: catsData } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "categories"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories`, token),
    enabled: !!token,
  });
  const rawCats = Array.isArray(catsData) ? catsData : (catsData?.categories ?? catsData?.data ?? []);
  const categories: any[] = [...rawCats].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name)));

  const selectedMenu = menuList.find((m: any) => String(m.info?.id ?? m.id) === selectedMenuId);
  const items: any[] = selectedMenu?.items ?? [];

  const categoryMap = new Map<number, string>(categories.map((c: any) => [c.id, c.name]));

  const { data: questionsData } = useQuery({
    queryKey: ["/api/admin/rmp", restaurantId, "questions"],
    queryFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/questions`, token),
    enabled: !!token,
  });
  const allQuestions: any[] = Array.isArray(questionsData) ? questionsData : (questionsData?.questions ?? []);
  const allAnswers: any[] = Array.isArray(questionsData) ? [] : (questionsData?.answers ?? []);

  const { data: expandedItemDetail, isLoading: expandedItemLoading } = useQuery({
    queryKey: ["/api/admin/rmp", restaurantId, "menus", selectedMenuId, "items", String(expandedItem)],
    queryFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${expandedItem}`, token),
    enabled: expandedItem !== null && !!selectedMenuId && !!token,
  });

  function extractDetailLinkedIds(detail: any): number[] {
    if (!detail) return [];
    const qs = detail.questions ?? detail.question_ids ?? detail.linked_questions;
    if (!Array.isArray(qs)) return [];
    return qs.map((q: any) => (typeof q === "number" ? q : q?.id)).filter(Boolean);
  }

  const invalidateMenus = () => qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"] });
  const invalidateItemDetail = () => {
    if (expandedItem !== null && selectedMenuId) {
      qc.invalidateQueries({ queryKey: ["/api/admin/rmp", restaurantId, "menus", selectedMenuId, "items", String(expandedItem)] });
    }
  };

  const linkModifierMutation = useMutation({
    mutationFn: ({ itemId, questionId }: { itemId: number; questionId: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/questions/link`, token, { method: "POST", body: JSON.stringify({ question_id: questionId }) }),
    onSuccess: () => { toast({ title: "Modifier linked" }); setLinkModifierItem(null); invalidateMenus(); invalidateItemDetail(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const unlinkModifierMutation = useMutation({
    mutationFn: ({ itemId, questionId }: { itemId: number; questionId: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/questions/unlink/${questionId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Modifier unlinked" }); invalidateMenus(); invalidateItemDetail(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const addItemMutation = useMutation({
    mutationFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items`, token, {
      method: "PUT",
      body: JSON.stringify({
        name: itemForm.name,
        description: itemForm.description,
        price: Number(itemForm.price),
        sort: itemForm.sort ? Number(itemForm.sort) : 0,
        category: itemForm.category ? Number(itemForm.category) : undefined,
        service: itemForm.service,
        type: itemForm.type || undefined,
        meta_category: itemForm.meta_category ? Number(itemForm.meta_category) : undefined,
        ...(itemForm.feeds_from !== "" && { feeds_from: Number(itemForm.feeds_from) }),
        ...(itemForm.feeds_to !== "" && { feeds_to: Number(itemForm.feeds_to) }),
        ...(itemForm.prep_time !== "" && { prep_time: Number(itemForm.prep_time) }),
        ...(itemForm.csr_note !== "" && { csr_note: itemForm.csr_note }),
      }),
    }),
    onSuccess: () => { toast({ title: "Item added" }); setAddItemOpen(false); setItemForm({ name: "", description: "", price: "", category: "", service: ["takeout", "delivery"], sort: "", type: "", meta_category: "", feeds_from: "", feeds_to: "", prep_time: "", csr_note: "" }); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: any }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}`, token, { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => { toast({ title: "Item updated" }); setEditItem(null); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: number) => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Item deleted" }); setDeleteItemConfirm(null); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const restoreItemMutation = useMutation({
    mutationFn: (itemId: number) => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/restore`, token, { method: "PUT" }),
    onSuccess: () => { toast({ title: "Item restored" }); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const duplicateItemMutation = useMutation({
    mutationFn: (itemId: number) => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/duplicate`, token, { method: "PUT" }),
    onSuccess: () => { toast({ title: "Item duplicated" }); invalidateMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  async function handleImageUpload(itemId: number, file: File) {
    setUploadingFor(itemId);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/image`, {
        method: "POST",
        headers: { "x-eatzy-token": token },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      toast({ title: "Image uploaded" });
      invalidateMenus();
    } catch (e: any) {
      toast({ title: "Image upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingFor(null);
    }
  }

  function serviceToggle(svc: string) {
    setItemForm(f => ({
      ...f,
      service: f.service.includes(svc) ? f.service.filter(s => s !== svc) : [...f.service, svc],
    }));
  }

  const serviceOptions = ["takeout", "delivery", "catering"];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <Select value={selectedMenuId} onValueChange={setSelectedMenuId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a menu" />
            </SelectTrigger>
            <SelectContent>
              {menuList.map((m: any) => {
                const id = String(m.info?.id ?? m.id);
                const name = m.info?.name ?? m.name;
                return <SelectItem key={id} value={id}>{name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        {selectedMenuId && (
          <Button size="sm" onClick={() => setAddItemOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        )}
      </div>

      {!selectedMenuId && <p className="text-sm text-muted-foreground py-4">Select a menu above to manage its items.</p>}

      {selectedMenuId && items.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">No items in this menu. Add one to get started.</p>
      )}

      {items.map((item: any) => {
        const itemId = item.id;
        const isDeleted = item.status === "deleted" || item.status === "inactive";
        const isExpanded = expandedItem === itemId;
        const thumb = item.media?.logo || item.media?.thumbnail || item.media?.item_thumbnail;
        const listLinkedIds: number[] = Array.isArray(item.questions)
          ? item.questions.map((q: any) => (typeof q === "number" ? q : q?.id)).filter(Boolean)
          : [];
        const detailLinkedIds = isExpanded ? extractDetailLinkedIds(expandedItemDetail) : [];
        const linkedIds: number[] = isExpanded && expandedItemDetail
          ? detailLinkedIds
          : listLinkedIds;
        const modCount = (() => {
          if (isExpanded && expandedItemDetail) return detailLinkedIds.length > 0 ? detailLinkedIds.length : 0;
          return listLinkedIds.length > 0 ? listLinkedIds.length : null;
        })();

        return (
          <Card key={itemId} className={isDeleted ? "opacity-60" : ""}>
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-start gap-3">
                {thumb && (
                  <img src={thumb} alt={item.name} className="w-14 h-14 rounded-md object-cover flex-shrink-0" loading="lazy" />
                )}
                {!thumb && (
                  <div className="w-14 h-14 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <UtensilsCrossed className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm">{item.name}</span>
                    {isDeleted && <Badge variant="destructive" className="text-xs">Deleted</Badge>}
                    {item.status && !isDeleted && <Badge variant="secondary" className="text-xs">{item.status}</Badge>}
                  </div>
                  {item.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>}
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                    {item.price != null && <span>Base: ${item.price}</span>}
                    {item.prices?.takeout?.retail != null && <span>Takeout: ${item.prices.takeout.retail}</span>}
                    {item.prices?.delivery?.retail != null && <span>Delivery: ${item.prices.delivery.retail}</span>}
                    {item.category && <span>Cat: {categoryMap.get(item.category) ?? `#${item.category}`}</span>}
                    <span className={modCount != null && modCount > 0 ? "text-foreground" : ""}>
                      {isExpanded && expandedItemLoading
                        ? "modifiers: …"
                        : modCount != null
                          ? `${modCount} modifier${modCount !== 1 ? "s" : ""}`
                          : "modifiers: —"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {!isDeleted && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => {
                        setEditItem({ ...item, menuId: selectedMenuId });
                        setItemForm({
                          name: item.name ?? "",
                          description: item.description ?? "",
                          price: String(item.price ?? ""),
                          category: String(item.category ?? ""),
                          service: item.service ?? ["takeout", "delivery"],
                          sort: String(item.sort ?? ""),
                          type: item.type ?? "",
                          meta_category: String(item.meta_category ?? ""),
                          feeds_from: item.feeds_from != null ? String(item.feeds_from) : "",
                          feeds_to: item.feeds_to != null ? String(item.feeds_to) : "",
                          prep_time: item.prep_time != null ? String(item.prep_time) : "",
                          csr_note: item.csr_note ?? "",
                        });
                      }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => duplicateItemMutation.mutate(itemId)} disabled={duplicateItemMutation.isPending}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => {
                        if (imageInputRef.current) {
                          imageInputRef.current.dataset.itemId = String(itemId);
                          imageInputRef.current.click();
                        }
                      }} disabled={uploadingFor === itemId}>
                        <ImageIcon className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteItemConfirm(item)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </>
                  )}
                  {isDeleted && (
                    <Button size="sm" variant="outline" onClick={() => restoreItemMutation.mutate(itemId)}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Restore
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => setExpandedItem(expandedItem === itemId ? null : itemId)}>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              {isExpanded && (
                <div className="mt-3 pt-3 border-t space-y-3">
                  <div className="text-xs text-muted-foreground space-y-1">
                    {item.category && <div>Category: {categoryMap.get(item.category) ?? `ID ${item.category}`}</div>}
                    {item.type && <div>Type: {item.type}</div>}
                    {item.meta_category != null && <div>Meta Category: {item.meta_category}</div>}
                    {item.service && <div>Services: {Array.isArray(item.service) ? item.service.join(", ") : item.service}</div>}
                    {item.prep_time != null && item.prep_time !== 0 && <div>Prep Time: {item.prep_time}s</div>}
                    {item.csr_note && <div>CSR Note: {item.csr_note}</div>}
                  </div>
                  {expandedItemLoading ? (
                    <p className="text-xs text-muted-foreground">Loading modifiers…</p>
                  ) : (
                    <ExpandedItemDetail
                      itemId={itemId}
                      itemName={item.name}
                      linkedQuestionIds={linkedIds}
                      allQuestions={allQuestions}
                      allAnswers={allAnswers}
                      onLinkRequest={(fullItem) => setLinkModifierItem({ ...fullItem, questions: linkedIds })}
                      onUnlink={(questionId) => unlinkModifierMutation.mutate({ itemId, questionId })}
                      unlinkPending={unlinkModifierMutation.isPending}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Hidden file input for image upload */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const itemId = Number(e.target.dataset.itemId);
          if (file && itemId) handleImageUpload(itemId, file);
          e.target.value = "";
        }}
      />

      {/* Add/Edit Item Dialog */}
      <Dialog open={addItemOpen || !!editItem} onOpenChange={(v) => { if (!v) { setAddItemOpen(false); setEditItem(null); } }}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle>{editItem ? "Edit Item" : "Add Item"}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={itemForm.name} onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
            <div className="space-y-1"><Label>Base Price</Label><Input type="number" step="0.01" value={itemForm.price} onChange={e => setItemForm(f => ({ ...f, price: e.target.value }))} /></div>
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={itemForm.category} onValueChange={v => setItemForm(f => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c: any) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Item Type</Label>
              <Select value={itemForm.type} onValueChange={v => setItemForm(f => ({ ...f, type: v === "__none__" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Select type (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Prepared Meal">Prepared Meal</SelectItem>
                  <SelectItem value="Bundle">Bundle</SelectItem>
                  <SelectItem value="Add-on">Add-on</SelectItem>
                  <SelectItem value="Side">Side</SelectItem>
                  <SelectItem value="Drink">Drink</SelectItem>
                  <SelectItem value="Dessert">Dessert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Meta Category ID</Label><Input type="number" value={itemForm.meta_category} onChange={e => setItemForm(f => ({ ...f, meta_category: e.target.value }))} placeholder="Optional numeric ID" /></div>
            <div className="space-y-1">
              <Label>Services</Label>
              <div className="flex flex-wrap gap-3">
                {serviceOptions.map(svc => (
                  <label key={svc} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={itemForm.service.includes(svc)} onChange={() => serviceToggle(svc)} className="rounded" />
                    {svc}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1"><Label>Sort Order</Label><Input type="number" value={itemForm.sort} onChange={e => setItemForm(f => ({ ...f, sort: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Feeds From</Label><Input type="number" value={itemForm.feeds_from} onChange={e => setItemForm(f => ({ ...f, feeds_from: e.target.value }))} placeholder="Optional" /></div>
              <div className="space-y-1"><Label>Feeds To</Label><Input type="number" value={itemForm.feeds_to} onChange={e => setItemForm(f => ({ ...f, feeds_to: e.target.value }))} placeholder="Optional" /></div>
            </div>
            <div className="space-y-1"><Label>Prep Time (seconds)</Label><Input type="number" value={itemForm.prep_time} onChange={e => setItemForm(f => ({ ...f, prep_time: e.target.value }))} placeholder="Optional" /></div>
            <div className="space-y-1"><Label>CSR Note</Label><Input value={itemForm.csr_note} onChange={e => setItemForm(f => ({ ...f, csr_note: e.target.value }))} placeholder="Internal note for CSR staff" /></div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
            <Button variant="outline" onClick={() => { setAddItemOpen(false); setEditItem(null); }}>Cancel</Button>
            <Button
              disabled={!itemForm.name || addItemMutation.isPending || updateItemMutation.isPending}
              onClick={() => {
                if (editItem) {
                  updateItemMutation.mutate({ itemId: editItem.id, data: {
                    name: itemForm.name,
                    description: itemForm.description,
                    price: Number(itemForm.price),
                    category: itemForm.category ? Number(itemForm.category) : editItem.category,
                    service: itemForm.service,
                    sort: itemForm.sort ? Number(itemForm.sort) : 0,
                    type: itemForm.type || null,
                    meta_category: itemForm.meta_category ? Number(itemForm.meta_category) : null,
                    ...(itemForm.feeds_from !== "" && { feeds_from: Number(itemForm.feeds_from) }),
                    ...(itemForm.feeds_to !== "" && { feeds_to: Number(itemForm.feeds_to) }),
                    ...(itemForm.prep_time !== "" && { prep_time: Number(itemForm.prep_time) }),
                    csr_note: itemForm.csr_note || null,
                  } });
                } else {
                  addItemMutation.mutate();
                }
              }}
            >
              {addItemMutation.isPending || updateItemMutation.isPending ? "Saving…" : editItem ? "Save Changes" : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Confirm */}
      <AlertDialog open={!!deleteItemConfirm} onOpenChange={v => !v && setDeleteItemConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete item?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteItemConfirm?.name}" will be soft-deleted. You can restore it later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteItemMutation.mutate(deleteItemConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Modifier Dialog */}
      <Dialog open={!!linkModifierItem} onOpenChange={v => !v && setLinkModifierItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Modifier Group</DialogTitle>
            <DialogDescription>Select a modifier group to link to "{linkModifierItem?.name}".</DialogDescription>
          </DialogHeader>
          {allQuestions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No modifier groups available for this restaurant.</p>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {allQuestions.map((q: any) => {
                const alreadyLinked = (linkModifierItem?.questions ?? []).some((lq: any) => lq === q.id || lq?.id === q.id);
                return (
                  <div key={q.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-0">
                    <div>
                      <p className="text-sm font-medium">{q.name}</p>
                      {q.text && <p className="text-xs text-muted-foreground">{q.text}</p>}
                    </div>
                    <Button
                      size="sm"
                      variant={alreadyLinked ? "outline" : "default"}
                      disabled={alreadyLinked || linkModifierMutation.isPending}
                      onClick={() => linkModifierMutation.mutate({ itemId: linkModifierItem.id, questionId: q.id })}
                    >
                      {alreadyLinked ? "Linked" : "Link"}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkModifierItem(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── CATEGORIES TAB ───────────────────────────────────────────────────────────

function CategoriesTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editCat, setEditCat] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [addForm, setAddForm] = useState({ name: "", description: "", sort_order: "" });
  const [editForm, setEditForm] = useState({ name: "", description: "", sort_order: "" });

  // Dedicated categories endpoint — categories are restaurant-level entities independent of menus
  const { data: catsData, isLoading, isError, error } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "categories"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories`, token),
    enabled: !!token,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "categories"] });

  // Handle both array and object responses from the Eatzy categories API
  const rawCategories = Array.isArray(catsData) ? catsData : (catsData?.categories ?? catsData?.data ?? []);
  const allCategories: any[] = [...rawCategories].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name)));

  const addMutation = useMutation({
    mutationFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories`, token, {
      method: "PUT",
      body: JSON.stringify({ ...addForm, sort_order: addForm.sort_order ? Number(addForm.sort_order) : undefined }),
    }),
    onSuccess: () => { toast({ title: "Category added" }); setAddOpen(false); setAddForm({ name: "", description: "", sort_order: "" }); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ catId, data }: { catId: number; data: any }) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories/${catId}`, token, { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => { toast({ title: "Category updated" }); setEditCat(null); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (catId: number) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories/${catId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Category deleted" }); setDeleteConfirm(null); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  if (isError) return <TabError error={error} label="categories" />;
  if (isLoading) return <div className="py-8 text-center text-muted-foreground">Loading categories…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{allCategories.length} categor{allCategories.length === 1 ? "y" : "ies"}</span>
        <Button size="sm" onClick={() => { setAddForm({ name: "", description: "", sort_order: "" }); setAddOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Add Category
        </Button>
      </div>

      {allCategories.length === 0 && !isLoading && (
        <p className="text-sm text-muted-foreground">No categories yet. Add one above.</p>
      )}

      <div className="space-y-2">
        {allCategories.map((cat: any) => (
          <Card key={cat.id}>
            <CardHeader className="py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-sm font-medium">{cat.name}</CardTitle>
                  {(cat.description || cat.sort_order != null) && (
                    <CardDescription className="text-xs mt-0.5">
                      {cat.description && <span>{cat.description}</span>}
                      {cat.description && cat.sort_order != null && <span> · </span>}
                      {cat.sort_order != null && <span>Sort: {cat.sort_order}</span>}
                    </CardDescription>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => {
                    setEditForm({ name: cat.name ?? "", description: cat.description ?? "", sort_order: String(cat.sort_order ?? "") });
                    setEditCat(cat);
                  }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteConfirm(cat)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Add Category Dialog */}
      <Dialog open={addOpen} onOpenChange={v => !v && setAddOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Description</Label><Input value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Sort Order</Label><Input type="number" value={addForm.sort_order} onChange={e => setAddForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={() => addMutation.mutate()} disabled={!addForm.name || addMutation.isPending}>
              {addMutation.isPending ? "Adding…" : "Add Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={!!editCat} onOpenChange={v => !v && setEditCat(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Category</DialogTitle><DialogDescription>{editCat?.name}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Sort Order</Label><Input type="number" value={editForm.sort_order} onChange={e => setEditForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCat(null)}>Cancel</Button>
            <Button
              onClick={() => updateMutation.mutate({ catId: editCat.id, data: { name: editForm.name, description: editForm.description, sort_order: editForm.sort_order ? Number(editForm.sort_order) : undefined } })}
              disabled={!editForm.name || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={v => !v && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteConfirm?.name}" will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate(deleteConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── MODIFIERS TAB ────────────────────────────────────────────────────────────

const EMPTY_Q_FORM = { name: "", text: "", format: "radio", required: true, min: "1", max: "1", max_per_answer: "", free_selections: "" };
const EMPTY_ANSWER_FORM = { text: "", price: "", sort: "", weight: "", trigger_question_id: "", feeds_from: "", feeds_to: "", prep_time: "", display_order: "" };

function ModifiersTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addQOpen, setAddQOpen] = useState(false);
  const [editQ, setEditQ] = useState<any>(null);
  const [deleteQConfirm, setDeleteQConfirm] = useState<any>(null);
  const [addAnswerFor, setAddAnswerFor] = useState<any>(null);
  const [editAnswer, setEditAnswer] = useState<{ question: any; answer: any } | null>(null);
  const [deleteAnswerConfirm, setDeleteAnswerConfirm] = useState<{ question: any; answer: any } | null>(null);
  const [qForm, setQForm] = useState({ ...EMPTY_Q_FORM });
  const [answerForm, setAnswerForm] = useState({ ...EMPTY_ANSWER_FORM });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/admin/rmp", restaurantId, "questions"],
    queryFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/questions`, token),
    enabled: !!token,
  });

  // The Eatzy restmanager API may return:
  //   A) A flat array of questions (data is the array itself)
  //   B) { questions: [...], answers: [...] } with separate flat answer list
  //   C) { questions: [{ ..., answers: [...] }] } with answers embedded per question (plural)
  //   D) { questions: [{ ..., answer: [...] }] }  with answers embedded per question (singular)
  const questions: any[] = Array.isArray(data) ? data : (data?.questions ?? []);
  const flatAnswers: any[] = Array.isArray(data) ? [] : (data?.answers ?? []);

  function answersFor(q: any): any[] {
    if (Array.isArray(q.answers)) return q.answers;
    if (Array.isArray(q.answer)) return q.answer;
    return flatAnswers.filter((a: any) => a.parent === q.id || a.question_id === q.id || a.question === q.id);
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: ["/api/admin/rmp", restaurantId, "questions"] });

  function buildQPayload() {
    return {
      name: qForm.name,
      text: qForm.text,
      format: qForm.format,
      required: qForm.required,
      min: Number(qForm.min),
      max: Number(qForm.max),
      ...(qForm.max_per_answer !== "" && { max_per_answer: Number(qForm.max_per_answer) }),
      ...(qForm.free_selections !== "" && { free_selections: Number(qForm.free_selections) }),
    };
  }

  function buildAnswerPayload() {
    return {
      text: answerForm.text,
      price: Number(answerForm.price),
      sort: answerForm.sort !== "" ? Number(answerForm.sort) : 0,
      weight: answerForm.weight !== "" ? Number(answerForm.weight) : 0,
      ...(answerForm.trigger_question_id !== "" && { triggers: Number(answerForm.trigger_question_id) }),
      ...(answerForm.feeds_from !== "" && { feeds_from: Number(answerForm.feeds_from) }),
      ...(answerForm.feeds_to !== "" && { feeds_to: Number(answerForm.feeds_to) }),
      ...(answerForm.prep_time !== "" && { prep_time: Number(answerForm.prep_time) }),
      ...(answerForm.display_order !== "" && { display_order: Number(answerForm.display_order) }),
    };
  }

  const createQMutation = useMutation({
    mutationFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/questions`, token, { method: "PUT", body: JSON.stringify(buildQPayload()) }),
    onSuccess: () => { toast({ title: "Question created" }); setAddQOpen(false); setQForm({ ...EMPTY_Q_FORM }); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const updateQMutation = useMutation({
    mutationFn: ({ questionId, payload }: { questionId: number; payload: any }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}`, token, { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { toast({ title: "Question updated" }); setEditQ(null); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteQMutation = useMutation({
    mutationFn: (questionId: number) => lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Question deleted" }); setDeleteQConfirm(null); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const restoreQMutation = useMutation({
    mutationFn: (questionId: number) => lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}/restore`, token, { method: "PUT" }),
    onSuccess: () => { toast({ title: "Question restored" }); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const addAnswerMutation = useMutation({
    mutationFn: ({ questionId }: { questionId: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}/answer`, token, { method: "PUT", body: JSON.stringify(buildAnswerPayload()) }),
    onSuccess: () => { toast({ title: "Answer added" }); setAddAnswerFor(null); setAnswerForm({ ...EMPTY_ANSWER_FORM }); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const updateAnswerMutation = useMutation({
    mutationFn: ({ questionId, answerId }: { questionId: number; answerId: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}/answer/${answerId}`, token, { method: "POST", body: JSON.stringify(buildAnswerPayload()) }),
    onSuccess: () => { toast({ title: "Answer updated" }); setEditAnswer(null); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteAnswerMutation = useMutation({
    mutationFn: ({ questionId, answerId }: { questionId: number; answerId: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}/answer/${answerId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Answer deleted" }); setDeleteAnswerConfirm(null); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const restoreAnswerMutation = useMutation({
    mutationFn: ({ questionId, answerId }: { questionId: number; answerId: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${questionId}/answer/${answerId}/restore`, token, { method: "PUT" }),
    onSuccess: () => { toast({ title: "Answer restored" }); invalidate(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  if (isError) return <TabError error={error} label="modifiers" />;
  if (isLoading) return <div className="py-8 text-center text-muted-foreground">Loading modifiers…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{questions.length} modifier group(s)</span>
        <Button size="sm" onClick={() => { setQForm({ ...EMPTY_Q_FORM }); setAddQOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Add Modifier Group
        </Button>
      </div>

      {questions.map((q: any) => {
        const isDeleted = q.status === "deleted" || q.status === "inactive";
        const qAnswers = answersFor(q);
        return (
          <Card key={q.id} className={isDeleted ? "opacity-60" : ""}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-medium">{q.name}</CardTitle>
                  <CardDescription className="text-xs">
                    {q.text && <span>{q.text} · </span>}
                    {q.format} · {q.required ? "required" : "optional"} · min {q.min} / max {q.max}
                    {q.max_per_answer != null && <span> · max/answer {q.max_per_answer}</span>}
                    {q.free_selections != null && <span> · {q.free_selections} free</span>}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-1">
                  {isDeleted ? (
                    <Button size="sm" variant="outline" onClick={() => restoreQMutation.mutate(q.id)}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Restore
                    </Button>
                  ) : (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => {
                        setQForm({ name: q.name, text: q.text ?? "", format: q.format, required: q.required, min: String(q.min), max: String(q.max), max_per_answer: q.max_per_answer != null ? String(q.max_per_answer) : "", free_selections: q.free_selections != null ? String(q.free_selections) : "" });
                        setEditQ(q);
                      }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteQConfirm(q)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {qAnswers.length === 0 ? (
                <p className="text-xs text-muted-foreground mb-2">No answers yet — add one below.</p>
              ) : (
                <div className="space-y-1 mb-3">
                  {qAnswers.map((a: any) => {
                    const aDeleted = a.status === "deleted";
                    return (
                      <div key={a.id} className={`flex flex-wrap items-center justify-between gap-2 py-1.5 border-b last:border-0 ${aDeleted ? "opacity-50" : ""}`}>
                        <div className="min-w-0">
                          <span className="text-sm font-medium">{a.text}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {a.prices?.takeout?.retail != null ? `$${a.prices.takeout.retail}` : (a.price != null ? `$${a.price}` : "")}
                            {a.sort != null && a.sort !== 0 && <span> · sort {a.sort}</span>}
                            {a.triggers != null && <span> · triggers Q{a.triggers}</span>}
                            {a.prep_time != null && a.prep_time !== 0 && <span> · prep {a.prep_time}s</span>}
                          </span>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {aDeleted ? (
                            <Button size="sm" variant="ghost" onClick={() => restoreAnswerMutation.mutate({ questionId: q.id, answerId: a.id })}>
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                          ) : (
                            <>
                              <Button size="icon" variant="ghost" onClick={() => {
                                setAnswerForm({
                                  text: a.text ?? "",
                                  price: String(a.price ?? ""),
                                  sort: String(a.sort ?? ""),
                                  weight: String(a.weight ?? ""),
                                  trigger_question_id: a.triggers != null ? String(a.triggers) : "",
                                  feeds_from: a.feeds_from != null ? String(a.feeds_from) : "",
                                  feeds_to: a.feeds_to != null ? String(a.feeds_to) : "",
                                  prep_time: a.prep_time != null ? String(a.prep_time) : "",
                                  display_order: a.display_order != null ? String(a.display_order) : "",
                                });
                                setEditAnswer({ question: q, answer: a });
                              }}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => setDeleteAnswerConfirm({ question: q, answer: a })}>
                                <Trash2 className="w-3 h-3 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!isDeleted && (
                <Button size="sm" variant="outline" onClick={() => { setAnswerForm({ ...EMPTY_ANSWER_FORM }); setAddAnswerFor(q); }}>
                  <Plus className="w-3 h-3 mr-1" /> Add Answer
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Add/Edit Question Dialog */}
      <Dialog open={addQOpen || !!editQ} onOpenChange={v => { if (!v) { setAddQOpen(false); setEditQ(null); } }}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0"><DialogTitle>{editQ ? "Edit Modifier Group" : "Add Modifier Group"}</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
            <div className="space-y-1"><Label>Name (internal) *</Label><Input value={qForm.name} onChange={e => setQForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Display Text</Label><Input value={qForm.text} onChange={e => setQForm(f => ({ ...f, text: e.target.value }))} /></div>
            <div className="space-y-1">
              <Label>Format</Label>
              <Select value={qForm.format} onValueChange={v => setQForm(f => ({ ...f, format: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="radio">Radio (single choice)</SelectItem>
                  <SelectItem value="checkbox">Checkbox (multi-choice)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={qForm.required} onCheckedChange={v => setQForm(f => ({ ...f, required: v }))} />
              <Label>Required</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Min Selections</Label><Input type="number" value={qForm.min} onChange={e => setQForm(f => ({ ...f, min: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Max Selections</Label><Input type="number" value={qForm.max} onChange={e => setQForm(f => ({ ...f, max: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Max per Answer</Label><Input type="number" value={qForm.max_per_answer} onChange={e => setQForm(f => ({ ...f, max_per_answer: e.target.value }))} placeholder="optional" /></div>
              <div className="space-y-1"><Label>Free Selections</Label><Input type="number" value={qForm.free_selections} onChange={e => setQForm(f => ({ ...f, free_selections: e.target.value }))} placeholder="optional" /></div>
            </div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
            <Button variant="outline" onClick={() => { setAddQOpen(false); setEditQ(null); }}>Cancel</Button>
            <Button
              disabled={!qForm.name || createQMutation.isPending || updateQMutation.isPending}
              onClick={() => {
                if (editQ) updateQMutation.mutate({ questionId: editQ.id, payload: buildQPayload() });
                else createQMutation.mutate();
              }}
            >
              {createQMutation.isPending || updateQMutation.isPending ? "Saving…" : editQ ? "Save Changes" : "Add Modifier Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Answer Dialog */}
      <Dialog open={!!addAnswerFor || !!editAnswer} onOpenChange={v => { if (!v) { setAddAnswerFor(null); setEditAnswer(null); } }}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle>{editAnswer ? "Edit Answer" : "Add Answer"}</DialogTitle>
            <DialogDescription>{editAnswer ? editAnswer.question.name : addAnswerFor?.name}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
            <div className="space-y-1"><Label>Answer Text *</Label><Input value={answerForm.text} onChange={e => setAnswerForm(f => ({ ...f, text: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Base Price</Label><Input type="number" step="0.01" value={answerForm.price} onChange={e => setAnswerForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" /></div>
            <div className="space-y-1">
              <Label>Triggers Question</Label>
              <Select value={answerForm.trigger_question_id || "__none__"} onValueChange={v => setAnswerForm(f => ({ ...f, trigger_question_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="None — does not trigger a new question" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {questions.filter((qq: any) => qq.id !== (editAnswer?.question?.id ?? addAnswerFor?.id)).map((qq: any) => (
                    <SelectItem key={qq.id} value={String(qq.id)}>{qq.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Feeds From</Label><Input type="number" value={answerForm.feeds_from} onChange={e => setAnswerForm(f => ({ ...f, feeds_from: e.target.value }))} placeholder="optional" /></div>
              <div className="space-y-1"><Label>Feeds To</Label><Input type="number" value={answerForm.feeds_to} onChange={e => setAnswerForm(f => ({ ...f, feeds_to: e.target.value }))} placeholder="optional" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Prep Time (secs)</Label><Input type="number" value={answerForm.prep_time} onChange={e => setAnswerForm(f => ({ ...f, prep_time: e.target.value }))} placeholder="optional" /></div>
              <div className="space-y-1"><Label>Display Order</Label><Input type="number" value={answerForm.display_order} onChange={e => setAnswerForm(f => ({ ...f, display_order: e.target.value }))} placeholder="optional" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Sort</Label><Input type="number" value={answerForm.sort} onChange={e => setAnswerForm(f => ({ ...f, sort: e.target.value }))} placeholder="0" /></div>
              <div className="space-y-1"><Label>Weight</Label><Input type="number" value={answerForm.weight} onChange={e => setAnswerForm(f => ({ ...f, weight: e.target.value }))} placeholder="0" /></div>
            </div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
            <Button variant="outline" onClick={() => { setAddAnswerFor(null); setEditAnswer(null); }}>Cancel</Button>
            <Button
              disabled={!answerForm.text || addAnswerMutation.isPending || updateAnswerMutation.isPending}
              onClick={() => {
                if (editAnswer) updateAnswerMutation.mutate({ questionId: editAnswer.question.id, answerId: editAnswer.answer.id });
                else addAnswerMutation.mutate({ questionId: addAnswerFor.id });
              }}
            >
              {addAnswerMutation.isPending || updateAnswerMutation.isPending ? "Saving…" : editAnswer ? "Save Changes" : "Add Answer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Question Confirm */}
      <AlertDialog open={!!deleteQConfirm} onOpenChange={v => !v && setDeleteQConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete modifier group?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteQConfirm?.name}" will be soft-deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteQMutation.mutate(deleteQConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Answer Confirm */}
      <AlertDialog open={!!deleteAnswerConfirm} onOpenChange={v => !v && setDeleteAnswerConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete answer?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteAnswerConfirm?.answer?.text}" will be soft-deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteAnswerMutation.mutate({ questionId: deleteAnswerConfirm!.question.id, answerId: deleteAnswerConfirm!.answer.id })}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function AdminRestaurantManage() {
  const { eatzyId } = useParams<{ eatzyId: string }>();
  const [, navigate] = useLocation();
  const { token } = useLAMToken();
  const [activeTab, setActiveTab] = useState("menu-editor");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { toast } = useToast();

  const {
    data: restaurantDetails,
    isError: lamDetailsError,
    error: lamDetailsErr,
    isLoading: lamDetailsLoading,
  } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", eatzyId, "details"],
    queryFn: () => token ? lamFetch(`/api/admin/lam/restaurants/${eatzyId}`, token) : null,
    enabled: !!token && !!eatzyId,
    retry: false,
  });
  // null = still resolving; true = confirmed LAM-registered; false = confirmed non-LAM (404)
  // We keep this null while loading so LAM sub-tabs don't mount before we know the outcome.
  const isLAMManaged: boolean | null = lamDetailsLoading
    ? null
    : !lamDetailsError || !(lamDetailsErr instanceof Error && lamDetailsErr.message.startsWith("404"));

  const { data: internalRestaurant, isLoading: internalRestaurantLoading } = useQuery<{ id: string; name: string } | undefined>({
    queryKey: ["/api/admin/restaurants"],
    select: (data: any) => data?.restaurants?.find((r: any) => String(r.kudozExternalId) === String(eatzyId)),
    enabled: !!eatzyId,
  });

  const restaurantName = restaurantDetails?.name ?? internalRestaurant?.name ?? `Restaurant #${eatzyId}`;

  const deleteRestaurantMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('DELETE', `/api/admin/restaurants/${id}`);
      return res.json() as Promise<{ deleted: boolean; restaurantId: string; reviewsDeleted: number }>;
    },
    onSuccess: (data) => {
      const msg = data.reviewsDeleted > 0
        ? `Restaurant deleted along with ${data.reviewsDeleted} review${data.reviewsDeleted === 1 ? '' : 's'}.`
        : 'Restaurant deleted.';
      toast({ title: 'Deleted', description: msg });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      navigate('/admin/restaurants');
    },
    onError: (error: any) => {
      toast({ title: 'Error deleting restaurant', description: error.message, variant: 'destructive' });
    },
  });

  if (!token) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4 max-w-sm">
            <ShieldAlert className="w-10 h-10 text-muted-foreground mx-auto" />
            <p className="font-medium">Insufficient permissions</p>
            <p className="text-sm text-muted-foreground">
              Please sign out and sign back in to access this restaurant.
            </p>
            <Button variant="outline" onClick={() => navigate("/admin/restaurants")}>
              Back to Restaurants
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/restaurants")}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">{restaurantName}</h1>
              <p className="text-sm text-muted-foreground">Eatzy ID: {eatzyId}</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="settings"><Settings className="w-3 h-3 mr-1" />Settings</TabsTrigger>
              <TabsTrigger value="menu-editor"><LayoutPanelLeft className="w-3 h-3 mr-1" />Menu Editor</TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="mt-6">
              <div className="space-y-10">
                {isLAMManaged === null ? (
                  <div className="py-8 flex items-center justify-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading settings…
                  </div>
                ) : !isLAMManaged ? (
                  <div className="flex gap-3 rounded-md border border-muted bg-muted/30 p-4">
                    <Info className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">This restaurant is not yet registered in the LAM system</p>
                      <p className="text-sm text-muted-foreground">
                        Configuration settings (zone, tax profile, price levels, rounding rules) are only
                        available for restaurants onboarded through the Eatzy Local Area Management system.
                        Restaurant {eatzyId} exists in the ordering system but has no LAM record yet.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        To enable these settings, the Eatzy team needs to create a LAM record for this
                        restaurant. The Menu Editor tab is fully functional in the meantime.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <section>
                      <div className="sticky top-0 bg-background z-10 pb-2 pt-1">
                        <h3 className="text-base font-semibold">Restaurant Config</h3>
                        <p className="text-sm text-muted-foreground">Zone, tax profile, timings, address, and operational settings.</p>
                      </div>
                      <ConfigTab restaurantId={eatzyId!} token={token!} />
                    </section>

                    <Separator />

                    <section>
                      <div className="sticky top-0 bg-background z-10 pb-2 pt-1">
                        <h3 className="text-base font-semibold">Price Levels</h3>
                        <p className="text-sm text-muted-foreground">Margin percentages per service type and price tier.</p>
                      </div>
                      <PriceLevelTab restaurantId={eatzyId!} token={token!} />
                    </section>

                    <Separator />

                    <section>
                      <div className="sticky top-0 bg-background z-10 pb-2 pt-1">
                        <h3 className="text-base font-semibold">Rounding Rules</h3>
                        <p className="text-sm text-muted-foreground">Control how calculated prices are rounded.</p>
                      </div>
                      <RoundingTab restaurantId={eatzyId!} token={token!} />
                    </section>
                  </>
                )}

                <Separator />

                <section>
                  <div className="pb-2 pt-1">
                    <h3 className="text-base font-semibold text-destructive">Danger Zone</h3>
                    <p className="text-sm text-muted-foreground">Irreversible and destructive actions.</p>
                  </div>
                  <Card className="border-destructive/40">
                    <CardContent className="pt-5">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <p className="font-medium text-sm">Delete this restaurant</p>
                          <p className="text-sm text-muted-foreground">
                            Permanently removes the restaurant and all associated reviews. This cannot be undone.
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          onClick={() => setDeleteConfirmOpen(true)}
                          disabled={!internalRestaurant || internalRestaurantLoading}
                        >
                          {internalRestaurantLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete Restaurant
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              </div>
            </TabsContent>

            <TabsContent value="menu-editor" className="mt-6">
              <MenuEditorErrorBoundary>
                <MenuEditorTab restaurantId={eatzyId!} token={token!} />
              </MenuEditorErrorBoundary>
            </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => { if (!open) setDeleteConfirmOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{restaurantName}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this restaurant and{' '}
              <strong>all reviews associated with it</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => internalRestaurant && deleteRestaurantMutation.mutate(internalRestaurant.id)}
              disabled={deleteRestaurantMutation.isPending}
            >
              {deleteRestaurantMutation.isPending ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting…
                </span>
              ) : (
                'Delete permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
